import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, PackageCheck, Ship, MessageSquare, Sprout, Warehouse, Boxes, ClipboardCheck, Container, Globe2, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useSettings } from "@/context/SettingsContext";
import { waLink } from "@/lib/wa";
import { IMAGES } from "@/constants/images";
import Reveal from "@/components/public/Reveal";
import SectionHeading from "@/components/public/SectionHeading";
import ProductCard from "@/components/public/ProductCard";
import EmptyState from "@/components/public/EmptyState";
import CTASection from "@/components/public/CTASection";

const FEATURES = [
  { icon: PackageCheck, title: "Reliable Supply", desc: "Access to available green coffee inventory and sourcing networks." },
  { icon: ShieldCheck, title: "Quality Control", desc: "Coffee is inspected and prepared according to agreed specifications." },
  { icon: Ship, title: "Export Ready", desc: "Coffee can be prepared, packed and coordinated for international shipment." },
  { icon: MessageSquare, title: "Transparent Communication", desc: "Clear specifications, quotations, documentation and shipment updates." },
];

const FLOW = [
  { icon: Sprout, label: "Origin" }, { icon: Boxes, label: "Sourcing" }, { icon: Warehouse, label: "Warehouse" },
  { icon: ClipboardCheck, label: "Quality Control" }, { icon: PackageCheck, label: "Packaging" },
  { icon: Container, label: "Container Loading" }, { icon: Ship, label: "Shipping" }, { icon: Globe2, label: "Buyer" },
];

function Counter({ value }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(value);
  useEffect(() => {
    const num = parseFloat(String(value).replace(/[^0-9.]/g, ""));
    if (isNaN(num)) { setDisplay(value); return; }
    const suffix = String(value).replace(/[0-9.,]/g, "").trim();
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        let start = 0; const dur = 1400; const t0 = performance.now();
        const tick = (t) => {
          const p = Math.min((t - t0) / dur, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setDisplay(Math.round(num * eased).toLocaleString() + (suffix ? " " + suffix : ""));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        obs.disconnect();
      }
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [value]);
  return <span ref={ref}>{display}</span>;
}

export default function Home() {
  const { t } = useLang();
  const { settings } = useSettings();
  const [products, setProducts] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, s] = await Promise.all([api.get("/products"), api.get("/statistics")]);
        setProducts(p.data);
        setFeatured(p.data.find((x) => x.featured) || p.data[0] || null);
        setStats(s.data);
      } catch (e) {} finally { setLoading(false); }
    })();
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative flex min-h-[92vh] items-center overflow-hidden bg-forest-dark">
        <img src={IMAGES.heroSack} alt="Indonesian green coffee beans in warehouse sacks" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-forest-dark via-forest-dark/85 to-forest-dark/40" />
        <div className="grain absolute inset-0 opacity-40" />
        <div className="relative mx-auto w-full max-w-[1400px] px-4 pt-28 pb-16 sm:px-8 lg:px-12">
          <Reveal>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-amber-soft/30 bg-amber-brand/10 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-amber-soft">
              {t("hero.trust")}
            </div>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="max-w-4xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-offwhite sm:text-5xl lg:text-7xl">
              {t("hero.title1")}<br />
              <span className="text-amber-soft">{t("hero.title2")}</span>
            </h1>
          </Reveal>
          <Reveal delay={240}>
            <p className="mt-7 max-w-2xl font-body text-base leading-relaxed text-offwhite/80 sm:text-lg">{t("hero.sub")}</p>
          </Reveal>
          <Reveal delay={360}>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Link to="/available-stock" data-testid="hero-stock-cta" className="group inline-flex items-center justify-center gap-2 rounded-full bg-amber-brand px-7 py-4 font-display text-sm font-bold uppercase tracking-wider text-espresso-dark transition-all hover:bg-amber-soft">
                {t("cta.stock")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/request-quote" data-testid="hero-quote-cta" className="inline-flex items-center justify-center gap-2 rounded-full border border-offwhite/30 px-7 py-4 font-display text-sm font-bold uppercase tracking-wider text-offwhite transition-all hover:bg-offwhite/10">
                {t("cta.quote")}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* VISUAL FLOW */}
      <section className="border-b border-forest/10 bg-espresso-dark py-8">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-12">
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-4 sm:justify-between">
            {FLOW.map((f, i) => (
              <React.Fragment key={f.label}>
                <div className="flex flex-col items-center gap-2 text-center">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full border border-amber-soft/25 bg-forest/40">
                    <f.icon className="h-5 w-5 text-amber-soft" />
                  </div>
                  <span className="font-mono text-[9px] uppercase tracking-wider text-offwhite/60">{f.label}</span>
                </div>
                {i < FLOW.length - 1 && <ArrowRight className="hidden h-4 w-4 shrink-0 text-offwhite/25 sm:block" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="mx-auto max-w-[1400px] px-4 py-20 sm:px-8 lg:px-12 lg:py-28">
        <SectionHeading eyebrow="Company Credibility" title={t("trust.title")} sub={t("trust.sub")} />
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={i * 90}>
              <div className="group h-full rounded-xl border border-forest/10 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-forest/25 hover:shadow-lg">
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-lg bg-forest text-offwhite transition-colors group-hover:bg-amber-brand group-hover:text-espresso-dark">
                  <f.icon className="h-6 w-6" />
                </div>
                <h3 className="font-display text-lg font-bold text-forest-dark">{f.title}</h3>
                <p className="mt-2 font-body text-sm leading-relaxed text-espresso/70">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* AVAILABLE STOCK */}
      <section className="bg-beige py-20 lg:py-28">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-12">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
            <SectionHeading eyebrow="Live Inventory" title="Coffee Ready for Your Next Order" sub={t("stock.sub")} />
            <Link to="/available-stock" className="shrink-0 whitespace-nowrap font-mono text-xs font-semibold uppercase tracking-wider text-forest hover:text-amber-brand">{t("cta.stock")} →</Link>
          </div>
          <div className="mt-12">
            {loading ? (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {[0,1,2,3].map((i) => <div key={i} className="h-80 animate-pulse rounded-xl bg-forest/5" />)}
              </div>
            ) : products.length === 0 ? (
              <EmptyState message={t("stock.empty")} testId="home-stock-empty" />
            ) : (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {products.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FEATURED ATENG */}
      {featured && (
        <section className="mx-auto max-w-[1400px] px-4 py-20 sm:px-8 lg:px-12 lg:py-28">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <div className="relative aspect-[4/3] overflow-hidden rounded-2xl bg-beige">
                <img src={featured.image || IMAGES.greenBeans} alt={featured.product_name} className="h-full w-full object-cover" />
                <div className="absolute bottom-4 left-4 rounded-full bg-amber-brand px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-espresso-dark">Featured Coffee</div>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="mb-3 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-amber-brand">Indonesian Arabica</div>
              <h2 className="font-display text-3xl font-extrabold tracking-tight text-forest-dark sm:text-4xl">{featured.product_name}</h2>
              <p className="mt-4 font-body text-base leading-relaxed text-espresso/75">{featured.description || "Indonesian Arabica green coffee sourced from selected producing areas in Indonesia."}</p>
              <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-4 font-mono text-sm">
                {[["Process", featured.process], ["Grade", featured.grade], ["Screen Size", featured.screen_size], ["Moisture", featured.moisture], ["Altitude", featured.altitude], ["Crop Year", featured.crop_year]].filter(([,v]) => v).map(([k,v]) => (
                  <div key={k}><div className="text-espresso/45 text-[11px] uppercase tracking-wider">{k}</div><div className="mt-0.5 font-semibold text-forest-dark">{v}</div></div>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to={`/product/${featured.slug || featured.id}`} className="inline-flex items-center gap-2 rounded-full bg-forest px-6 py-3 font-display text-xs font-bold uppercase tracking-wider text-offwhite transition-all hover:bg-forest-dark">{t("cta.specs")} <ArrowUpRight className="h-4 w-4" /></Link>
                <Link to="/request-quote" className="inline-flex items-center gap-2 rounded-full border border-forest/25 px-6 py-3 font-display text-xs font-bold uppercase tracking-wider text-forest transition-all hover:bg-forest/5">{t("cta.sample")}</Link>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* WAREHOUSE */}
      <section className="relative overflow-hidden bg-forest-dark py-20 lg:py-28">
        <img src={settings.home_feature_image || "/company/d.jpg"} alt="Inside the Warehouse Coffee facility" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="grain absolute inset-0 opacity-40" />
        {settings.home_feature_caption && (
          <div className="absolute bottom-6 left-4 z-10 max-w-xs rounded-lg border border-offwhite/15 bg-forest-dark/70 px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-amber-soft backdrop-blur-sm sm:left-8 lg:left-12">
            {settings.home_feature_caption}
          </div>
        )}
        <div className="relative mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <SectionHeading light eyebrow="Our Warehouse" title="See Where Your Coffee Comes From" sub="Reliable coffee supply begins with proper storage, preparation and control. Explore our warehouse operations, from green bean storage to loading preparation." />
            <Reveal delay={120}>
              <div className="flex flex-col gap-3">
                {["Storage & Humidity Control", "Sorting & Quality Inspection", "Packaging & Preparation", "Loading Coordination"].map((s) => (
                  <div key={s} className="flex items-center gap-3 rounded-lg border border-offwhite/10 bg-forest/30 px-5 py-4">
                    <div className="h-2 w-2 rounded-full bg-amber-soft" />
                    <span className="font-body text-sm text-offwhite/85">{s}</span>
                  </div>
                ))}
                <Link to="/our-warehouse" className="mt-2 inline-flex w-fit items-center gap-2 font-mono text-xs font-semibold uppercase tracking-wider text-amber-soft hover:text-amber-brand">Inside Warehouse Coffee →</Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* STATISTICS */}
      {stats.length > 0 && (
        <section className="mx-auto max-w-[1400px] px-4 py-20 sm:px-8 lg:px-12">
          <SectionHeading align="center" eyebrow="Our Track Record" title="Built on Real Activity" />
          <div className="mt-12 grid grid-cols-2 gap-6 lg:grid-cols-4">
            {stats.map((st, i) => (
              <Reveal key={st.id} delay={i * 80}>
                <div className="rounded-xl border border-forest/10 bg-white p-8 text-center">
                  <div className="font-display text-3xl font-extrabold text-forest lg:text-5xl"><Counter value={st.value} /></div>
                  <div className="mt-2 font-mono text-[11px] uppercase tracking-wider text-espresso/55">{st.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* WHY */}
      <section className="bg-beige py-20 lg:py-28">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-12">
          <SectionHeading eyebrow="Why Warehouse Coffee" title="A Real Supplier With an Organized Export Process" sub="We built our business around transparency and reliable supply — not exaggerated claims." />
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {[["Established 2020", "A physical green coffee supply and trading business with actual inventory."],
              ["Transparent Specifications", "Clear grade, moisture, screen size and process details for every lot."],
              ["Export Preparation", "Documentation, packaging and freight coordination for international buyers."]].map(([tl, ds], i) => (
              <Reveal key={tl} delay={i * 90}>
                <div className="h-full rounded-xl border border-forest/10 bg-white p-7">
                  <div className="font-mono text-xs font-semibold text-amber-brand">0{i+1}</div>
                  <h3 className="mt-3 font-display text-lg font-bold text-forest-dark">{tl}</h3>
                  <p className="mt-2 font-body text-sm leading-relaxed text-espresso/70">{ds}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
